import { motion } from "motion/react";
import { X, Check } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { type PanelCard } from "./AddCardsPanel";
import { ConfigurableCardGrid, type CardSize } from "../../components/ConfigurableCardGrid";

/* In-page "Extra cards" surface — ALL available cards, selected and unselected.
     • shows every available card at its real size; cards already on the dashboard
       start ticked (selected), the rest start unticked (unselected);
     • tick/untick to choose what's shown and drag to arrange, then press Done —
       the ticked set (in arranged order) becomes the new dashboard, so this both
       adds newly-ticked cards and removes any you untick;
     • opening it from a card's "View detail" spotlights that card (the rest are
       blurred) until you click anywhere to dismiss the spotlight. */
export function ExtraCardsPanel({
  active, cards, renderMap, sizeOf, onApply, onClose, highlightId, onClearHighlight,
}: {
  active: string[];                                // ids currently on the dashboard
  cards: PanelCard[];                              // full catalog
  renderMap: Record<string, ReactNode>;            // live card JSX by id
  sizeOf: (id: string) => CardSize | undefined;    // card footprint
  onApply: (ids: string[]) => void;                // commit final dashboard ids
  onClose: () => void;                             // cancel (no changes)
  highlightId?: string;                            // card to spotlight (from View detail)
  onClearHighlight?: () => void;                   // notify parent the spotlight was dismissed
}) {
  const allIds = cards.map((c) => c.id);
  const cardMap: Record<string, PanelCard> = Object.fromEntries(cards.map((c) => [c.id, c]));

  /* Every available card, with the on-dashboard ones first (in their dashboard
     order), then the rest. Selected = currently on the dashboard. */
  const [order, setOrder] = useState<string[]>(() => [
    ...active.filter((id) => allIds.includes(id)),
    ...allIds.filter((id) => !active.includes(id)),
  ]);
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(active.filter((id) => allIds.includes(id))),
  );
  /* Spotlight from a "View detail" deep-link; cleared on any click in the panel. */
  const [highlight, setHighlight] = useState<string | undefined>(highlightId);
  useEffect(() => setHighlight(highlightId), [highlightId]);

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clearHighlight = () => {
    if (!highlight) return;
    setHighlight(undefined);
    onClearHighlight?.();
  };

  const apply = () => {
    /* The ticked cards (in arranged order) become the new dashboard — adds the
       newly-ticked and removes any that were unticked. */
    onApply(order.filter((id) => checked.has(id)));
    onClose();
  };

  return (
    <motion.div
      className="rounded-2xl border border-border bg-card p-5"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      onClick={clearHighlight}
    >
      {/* header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-foreground">Manage dashboard </h3>
            <span className="rounded-full bg-indigo-500/10 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">
              {checked.size} selected
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Choose which cards show on your dashboard  tick to add, drag to arrange, then press Done
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={apply}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check className="size-3.5" />
            Done
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Cancel"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* every available card at its real size; ticked = on the dashboard */}
      {order.length === 0 ? (
        <div className="flex h-32 items-center justify-center text-center">
          <span className="text-sm text-muted-foreground">
            No cards available to configure.
          </span>
        </div>
      ) : (
        <ConfigurableCardGrid
          items={order}
          renderMap={renderMap}
          editing
          onRemove={() => {}}
          onReorder={setOrder}
          sizeOf={sizeOf}
          selectable
          checked={checked}
          onToggleCheck={toggle}
          renderAbout={(id) => {
            const c = cardMap[id];
            if (!c) return null;
            return (
              <div className="max-w-[260px] space-y-1 text-left">
                <p className="text-xs font-semibold">{c.title}</p>
                <p className="text-xs opacity-80">{c.about ?? c.description}</p>
              </div>
            );
          }}
          highlightId={highlight}
        />
      )}
    </motion.div>
  );
}
